import { zoomToVis } from "@/animations/zoomToVis.js";
import { canv } from "@/canv/global.js";
import { getViewport } from "@/canvasController/helpers";
import { eventEmitter } from "@/eventEmitter";
import { generateColor } from "@/helpers/generateColor.js";
import { MultiUser } from "./index";

export class multiManager {
    multiUsersMap: Map<string, MultiUser> = new Map();
    viewSubscribeUID: string = "";
    disableMultiCursors: boolean = false;

    private lastSentPosition: any = null;
    private rafId: number | null = null;
    private hasPendingUpdate: boolean = false;
    private lastSentTime: number = 0;
    private lastSentViewport: any = null;
    private minIntervalMs: number = 200;
    autoFollowWasApplied: boolean = false;

    constructor() {
        eventEmitter.dispatch("multi.cursors", {
            disableMultiCursors: this.disableMultiCursors,
        });
        this.init();
    }

    init() {
        this.scheduleRAF();
    }

    enableCursorUpdate = () => {
        this.hasPendingUpdate = true;
    };

    private hasChanged(pos: { x: number; y: number }, viewport: any): boolean {
        const posChanged =
            !this.lastSentPosition ||
            this.lastSentPosition.x !== pos.x ||
            this.lastSentPosition.y !== pos.y;

        const viewportChanged =
            !this.lastSentViewport ||
            JSON.stringify(this.lastSentViewport) !== JSON.stringify(viewport);

        return posChanged || viewportChanged;
    }

    private scheduleRAF() {
        this.rafId = requestAnimationFrame((timestamp) => {
            if (this.hasPendingUpdate && timestamp - this.lastSentTime >= this.minIntervalMs) {
                const currentPos = canv.multiManager.getCursorPosition();
                const currentViewport = getViewport();

                if (this.hasChanged(currentPos, currentViewport)) {
                    canv.intercom.viewMessage(currentPos, currentViewport);
                    this.lastSentPosition = currentPos;
                    this.lastSentTime = timestamp;
                    this.lastSentViewport = currentViewport;
                }

                this.hasPendingUpdate = false;
            }

            this.scheduleRAF();
        });
    }

    destroy() {
        if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    }

    addMultiUser(data) {
        const sessionUID = data.sessionUID;

        if (this.multiUsersMap.has(sessionUID)) {
            return;
        }

        const isCurrentUser = canv.getInitDataProp("userToken") === data.token;
        const userColor = generateColor(isCurrentUser ? sessionUID : data.token || sessionUID);

        const newMultiUser = new MultiUser(
            data.name,
            data.login,
            data.sessionUID,
            data.token,
            userColor,
        );

        this.multiUsersMap.set(sessionUID, newMultiUser);

        this.notifyUsersChanged();

        canv.applyHighload();
    }

    deleteMultiUser(sessionUID) {
        if (!this.multiUsersMap.has(sessionUID)) {
            return;
        }

        canv.appInterface.usersWindow?.close();

        this.multiUsersMap.delete(sessionUID);

        this.notifyUsersChanged();

        if (this.viewSubscribeUID === sessionUID) {
            this.resetViewSubscribe();
        }

        canv.applyHighload();
        canv.redraw();
    }

    resetMultiUsers() {
        this.multiUsersMap.clear();
        this.destroy();
        this.notifyUsersChanged();
        canv.appInterface.usersWindow?.close();
        this.resetViewSubscribe();
        canv.redraw();
        canv.redraw(true);
    }

    private notifyUsersChanged() {
        eventEmitter.dispatch("multi.users.sessions", {
            users: Array.from(this.multiUsersMap.values()),
        });
        canv.appInterface.usersWindow?.reFillUsers();
    }

    findMultiUser(sessionUID: string): MultiUser | null {
        return this.multiUsersMap.get(sessionUID) ?? null;
    }

    applySelectData(data) {
        const multiUser = this.findMultiUser(data.sessionUID);
        if (multiUser) {
            multiUser.setSelectedGroups(data.selectedGroups);
            canv.redraw();
        }
    }

    applyViewData(data) {
        let multiUser = this.findMultiUser(data.sessionUID);

        if (!multiUser) {
            this.addMultiUser(data);
            multiUser = this.findMultiUser(data.sessionUID);
        }

        if (multiUser) {
            multiUser.updateVirtualCursor(data.cursorPosition.x, data.cursorPosition.y);
            multiUser.updateViewData(data.viewPosition);
        }

        const currentUserToken = canv.getInitDataProp("userToken");
        const followTargetToken = canv.getInitDataProp("followTargetToken");
        const isCurrentTokenUnequalToTargetToken = followTargetToken !== currentUserToken;
        const isUserTokenEqualToTargetToken = data.token === followTargetToken;
        const isSubscribeUIDUnequalToUserUID = this.viewSubscribeUID !== data.sessionUID;

        if (
            followTargetToken &&
            !this.autoFollowWasApplied &&
            isCurrentTokenUnequalToTargetToken &&
            isUserTokenEqualToTargetToken &&
            isSubscribeUIDUnequalToUserUID
        ) {
            this.viewSubscribeUID = data.sessionUID;
            this.autoFollowWasApplied = true;
            eventEmitter.dispatch("multi.users.trackingSession", { session: data.sessionUID });
        }

        if (multiUser) {
            this.applyViewSubscribe();
        }
    }

    changeVisibilityCursor(isVisible) {
        for (const multiUser of this.multiUsersMap.values()) {
            multiUser.changeVisibility(isVisible);
        }
    }

    applyViewSubscribe() {
        if (!this.viewSubscribeUID) {
            return;
        }

        const multiUser = this.findMultiUser(this.viewSubscribeUID);

        if (!multiUser) {
            return;
        }

        const screenData = {
            simulatedSelectingStartX: -canv.m[4] / canv.m[0],
            simulatedSelectingStartY: -canv.m[5] / canv.m[3],
            simulatedSelectingWidth: canv.canvas.width / canv.m[0],
            simulatedSelectingHeight: canv.canvas.height / canv.m[3],
        };

        const selectedProps = multiUser.viewData;

        // Центр вьюпорта другого пользователя
        const targetCenterX = selectedProps["viewportStartX"] + selectedProps["viewportWidth"] / 2;
        const targetCenterY = selectedProps["viewportStartY"] + selectedProps["viewportHeight"] / 2;

        // выбираем меньший коэффициент, чтобы видеть все что видит другой пользователь
        // если выбрать бОльший - будем видеть в том же масштабе, что и другой, но часть контента может обрезаться
        const ratio = Math.min(
            screenData.simulatedSelectingWidth / selectedProps["viewportWidth"],
            screenData.simulatedSelectingHeight / selectedProps["viewportHeight"],
        );

        const goTo = {
            0: canv.m[0] * ratio,
            3: canv.m[3] * ratio,
        };
        goTo["4"] = -(targetCenterX - screenData.simulatedSelectingWidth / (2 * ratio)) * goTo["0"];
        goTo["5"] =
            -(targetCenterY - screenData.simulatedSelectingHeight / (2 * ratio)) * goTo["3"];

        const animation = new zoomToVis(goTo);
        canv.animator.deleteAnimation("zoomTo");
        canv.animator.addAnimation(animation);
        canv.redraw();
    }

    tryViewSubscribe(sessionUID) {
        const shouldFocus = this.viewSubscribeUID !== sessionUID;
        this.resetViewSubscribe();

        if (!shouldFocus) {
            return;
        }

        const selectedUserLine = document.querySelector(`[sessionUID="${sessionUID}"]`);
        if (selectedUserLine) {
            selectedUserLine.classList.add("usersWindowItemSelectorOn");
        }

        eventEmitter.dispatch("multi.users.trackingSession", {
            session: sessionUID,
        });

        this.viewSubscribeUID = sessionUID;
        this.applyViewSubscribe();
    }

    resetViewSubscribe() {
        this.viewSubscribeUID = "";
        eventEmitter.dispatch("multi.users.trackingSession", { session: null });

        const selectedUserLine = document.querySelector(".usersWindowItemSelectorOn");
        if (selectedUserLine) {
            selectedUserLine.classList.remove("usersWindowItemSelectorOn");
        }

        canv.animator.deleteAnimation("zoomTo");
    }

    getCursorPosition() {
        const pt = canv.actionManager.getActionPoint();

        return { x: pt.x, y: pt.y };
    }

    draw() {
        this.drawView();

        if (this.disableMultiCursors || canv.highloadMode) return;
        for (const multiUser of this.multiUsersMap.values()) {
            multiUser.draw();
        }
    }

    drawView() {
        if (!this.viewSubscribeUID) return;

        const multiUser = this.findMultiUser(this.viewSubscribeUID);
        if (!multiUser) return;

        const screenData = this.calculateScreenData();

        canv.ctx.strokeStyle = multiUser.color || "red";
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(12);
        canv.ctx.strokeRect(
            screenData.simulatedSelectingStartX,
            screenData.simulatedSelectingStartY,
            screenData.simulatedSelectingWidth,
            screenData.simulatedSelectingHeight,
        );
    }

    private calculateScreenData() {
        return {
            simulatedSelectingStartX: -canv.m[4] / canv.m[0],
            simulatedSelectingStartY: -canv.m[5] / canv.m[3],
            simulatedSelectingWidth: canv.canvas.width / canv.m[0],
            simulatedSelectingHeight: canv.canvas.height / canv.m[3],
        };
    }

    setDisableMultiCursors(value) {
        if (this.disableMultiCursors === value) {
            return;
        }
        this.disableMultiCursors = value;

        eventEmitter.dispatch("multi.cursors", {
            disableMultiCursors: value,
        });

        canv.redraw();
    }
}
